import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { short } from "@/components/LiveIntelTable";
import { fmtMins, PERIODS } from "@/lib/liveProfile";
import { LiveTag } from "@/components/c360/profile/LiveExtras";
import { bl, useBlur } from "@/lib/c360Mine";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const day = (d) => { const [, m, dd] = d.split("-").map(Number); return `${dd} ${MON[m - 1]}`; };
const SIZE = 10;
const th = "px-4 py-3 text-left font-normal mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap";

export function LiveHistory({ lives, count, period }) {
  const [open, setOpen] = useState(true);
  const blur = useBlur();
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [period]);
  const rows = lives.slice(0, count);
  const pages = Math.max(1, Math.ceil(count / SIZE));
  const shown = rows.slice((page - 1) * SIZE, page * SIZE);
  const words = PERIODS.find((x) => x.k === period).words;
  return (
    <section className="border hairline rounded-md overflow-hidden" style={{ background: "var(--surface)" }} data-testid="lp-history">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="lp-history-toggle" className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left">
        <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="mono text-[11px] uppercase tracking-[0.3em]" style={{ color: "#00F0FF" }}>Live stream history</span>
          <span className="mono text-[11px] text-zinc-400" data-testid="lp-history-sub">{count.toLocaleString()} live stream{count === 1 ? "" : "s"} on KOL Radar · {words}</span>
        </span>
        <ChevronDown size={16} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="overflow-x-auto border-t hairline">
            <table className="w-full text-sm min-w-[1180px]" data-testid="lp-history-table">
              <thead><tr className="border-b hairline">
                <th className={th}>Date</th><th className={th}>Live stream</th><th className={th}>Promoted</th><th className={th}>Length</th><th className={th}>Avg live</th><th className={th}>Peak</th><th className={th}>Views</th>
                <th className={th}>Likes</th><th className={th}>Comments</th><th className={th}>Engagement</th><th className={th}>Chat/min</th>
              </tr></thead>
              <tbody>
                {shown.map((l) => {
                  const eng = l.likes > 0 && l.views ? `${(((l.likes + l.comments) * 100) / l.views).toFixed(2)}%` : "—";
                  return (
                    <tr key={l.vid || l.url} className="border-t hairline hover:bg-white/[0.02]" data-testid="lp-history-row" data-vid={l.vid}>
                      <td className="px-4 py-3 mono text-zinc-400 whitespace-nowrap align-top">{day(l.date)}</td>
                      <td className="px-4 py-3 align-top">
                        <a href={l.url} target="_blank" rel="noreferrer" className={`flex items-start gap-3 text-zinc-200 hover:text-cyan-300 ${bl(blur, "link")}`} data-testid="lp-history-link">
                          <span className="mono text-[9px] tracking-wider px-1.5 py-0.5 mt-0.5 rounded shrink-0" style={{ color: "#00F0FF", background: "rgba(0,240,255,0.1)" }}>LIVE</span>
                          <span className={`line-clamp-2 ${bl(blur)}`} data-testid="lp-history-title">{l.title}</span>
                          <LiveTag tag={l.tag} />
                        </a>
                      </td>
                      <td className="px-4 py-3 mono text-[11px] text-zinc-400 align-top">{l.ex?.length ? l.ex.join(", ") : "—"}</td>
                      <td className="px-4 py-3 mono text-zinc-300 whitespace-nowrap align-top">{fmtMins(l.mins)}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top" data-testid="lp-history-avg">{l.avg != null ? l.avg.toLocaleString() : "—"}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top">{l.peak != null ? l.peak.toLocaleString() : "—"}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top">{short(l.views)}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top" data-testid="lp-history-likes">{l.likes > 0 ? l.likes.toLocaleString() : "Hidden"}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top">{l.comments.toLocaleString()}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top" data-testid="lp-history-eng">{eng}</td>
                      <td className="px-4 py-3 mono text-zinc-300 align-top">{l.chat != null ? Math.round(l.chat * 10) / 10 : "—"}</td>
                    </tr>
                  );
                })}
                {!count && <tr><td colSpan={11} className="px-4 py-10 text-center text-zinc-500" data-testid="lp-history-empty">No live streams on KOL Radar in this period</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t hairline flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="mono text-zinc-400" data-testid="lp-history-count">
              {count ? `Showing ${(page - 1) * SIZE + 1}–${Math.min(count, page * SIZE)} of ${count.toLocaleString()} live streams · ${words}` : `0 live streams · ${words}`}
            </span>
            <span className="flex items-center gap-3">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="lp-history-prev" className="mono text-zinc-300 disabled:text-zinc-600">Prev</button>
              <span className="mono text-zinc-400" data-testid="lp-history-page">{page} / {pages}</span>
              <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="lp-history-next" className="mono px-3 py-1.5 rounded-md border hairline text-zinc-200 disabled:text-zinc-600">Next</button>
            </span>
          </div>
        </>
      )}
    </section>
  );
}
